import React from 'react';
import {
  string, func, bool, number, shape,
} from 'prop-types';
import axios from 'axios';
import Autosuggest from 'react-autosuggest';

import ThreeBounce from '../../ThreeBounce';
import { alwaysShowSchoolNotFound, defaultCAccounts, defaultInstitutionId } from '../../../destructured-front-end-config';
import BasePureComponent from '../../../BasePureComponent';
import { AutosuggestWrap } from './SchoolSearchV2Styles';

const isNullOrUndefined = (val) => val === undefined || val === null;

const keyExistsAsString = (val) => typeof val !== 'undefined' && typeof val === 'string';

// Use your imagination to render suggestions.
export default class SchoolSearchV2 extends BasePureComponent {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value || '',
      suggestions: [],
      isLoading: false,
      previousSearch: '',
      instType: props.brand.short === 'bfw' ? 'hs' : 'college',
    };
    this.lastRequestId = null;
  }

  onChange = (event, { schoolName, cAccount, institutionId }) => {
    this.props.onChange({ schoolName, cAccount, institutionId });
    this.setState({
      value: schoolName,
    });
  }

  handleSchoolChange = (source, change) => {
    // Keydowns also generate change events with undefined newValue -- ignore
    if (isNullOrUndefined(change.newValue) && isNullOrUndefined(change.name)) return;

    // scenarios where newValue is present and defined are changes
    if (keyExistsAsString(change.newValue)) {
      this.onChange(source, {
        schoolName: change.newValue,
        cAccount: (change.cAccount || defaultCAccounts[0]),
        // these won't have a value for inst id, but we need to
        // overwrite any existing value in state
        institutionId: defaultInstitutionId,
      });
      return;
    }
    // this is basically clicking on a dropdown item
    this.onChange(source, {
      schoolName: change.prettyName || change.name,
      cAccount: change.cAccount,
      institutionId: change.accountId,
    });
    this.handleSelect(change);
  }

  onSuggestionsFetchRequested = async ({ value }) => {
    const { searchUrl, iamApiUrl } = this.props;
    const { instType, previousSearch } = this.state;
    if (this.lastRequestId !== null) {
      clearTimeout(this.lastRequestId);
    }
    // don't initiate new search if text hasn't changed
    if (previousSearch.trim() === value.trim()) {
      return null;
    }
    let url = `${iamApiUrl.endsWith('/') ? iamApiUrl.substr(0, searchUrl.length - 1) : iamApiUrl}/api/v2/school-search?schoolType=${instType === 'college' ? 'he' : 'hs'}&searchQuery=${encodeURIComponent(value)}`;

    await this.setState({ isLoading: true });

    if (process.env.MOCK_SCHOOLSEARCH === 'true') {
      this.lastRequestId = setTimeout(() => {
        const matchedSchools = require(`../../../../mocks/schools-${instType}.json`).results.filter((s) => s.name.toLowerCase().includes(value.toLowerCase()));
        const snfItem = {
          name: 'School Not Found',
          cAccount: defaultCAccounts[0],
          accountId: defaultInstitutionId,
        };

        const suggestions = matchedSchools?.length
          ? matchedSchools
          : [snfItem];

        if (alwaysShowSchoolNotFound && (suggestions[0].name !== snfItem.name)) {
          suggestions.push(snfItem);
        }

        this.setState({
          suggestions,
          previousSearch: value,
          isLoading: false,
        });
      }, 300);
    } else {
      this.lastRequestId = setTimeout(() => {
        axios({
          method: 'GET',
          url,
          // allow 2XX and 5XX status codes
          validateStatus: (status) => (status >= 200 && status < 300) || (status >= 500 && status < 600),
        }).then((response) => {
          const snfItem = {
            name: 'School Not Found',
            prettyName: 'School Not Found',
            objectId: null,
            cAccount: defaultCAccounts[0],
            accountId: defaultInstitutionId,
          };

          const suggestions = response.data?.results || [snfItem];

          // if FEATUREFLAG_ALWAYS_SHOW_SCHOOL_NOT_FOUND is true, and the first
          // suggestion is not the school not found item, add it to the list
          if (alwaysShowSchoolNotFound && (suggestions[0].name !== snfItem.name)) {
            suggestions.push(snfItem);
          }

          this.setState({
            suggestions,
            previousSearch: value,
            isLoading: false,
          });
        }).catch((error) => {
          console.error(error);
        });
      }, 300);
    }
    return this.lastRequestId;
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  }

  getSuggestionValue = (suggestion) => {
    const lcCAccount = suggestion.cAccount?.toLowerCase();

    // return this.state.value if nothing was actually selected from the dropdown
    if (defaultCAccounts.some((c) => c.toLowerCase() === lcCAccount) && (suggestion.name?.toLowerCase() !== 'school not found')) {
      return this.state.value;
    }
    // this might be uncomfortably hacky
    this.handleSchoolChange('keyDownOrUp', suggestion);
    return suggestion.text;
  };

  handleSelect = (suggestion) => {
    const lcCAccount = suggestion.cAccount?.toLowerCase();

    if (!defaultCAccounts.some((c) => c.toLowerCase() === lcCAccount)) {
      this.props.onSelect(suggestion);
    } else {
      this.props.onSelect({
        name: suggestion.name,
        cAccount: defaultCAccounts[0],
        accountId: suggestion.accountId || defaultInstitutionId,
      });
    }
  }

  renderSuggestion = ({
    // eslint-disable-next-line react/prop-types
    name, prettyName, city, state, stateCode,
  }) => {
    let cityString = `${city}, ${stateCode || state}`;
    if (stateCode === false || state === null) {
      cityString = city;
    } else if (!city) {
      cityString = '';
    }
    return (
      <div className="schoolItem">
        <span>{prettyName || name}</span>
        <span className="cityState">{cityString}</span>
      </div>
    );
  }

  render() {
    const {
      value, suggestions, isLoading, maxLength,
    } = this.state;
    const inputProps = {
      placeholder: 'Start typing your school name',
      value,
      id: this.props.id,
      onChange: (e, change) => this.handleSchoolChange('onChange', change),
      onBlur: this.props.onBlur,
      disabled: this.props.disabled,
      maxLength: this.props.maxLength,
    };
    return (
      <AutosuggestWrap width={this.props.width} hasError={this.props.hasError}>
        {isLoading && <div className="loader"><ThreeBounce color="#da1b2c" size="12" /></div>}
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={(e, { suggestion }) => this.handleSchoolChange('onSuggestionSelected', suggestion)}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps}
          maxLength={maxLength}
        />
      </AutosuggestWrap>
    );
  }
}

SchoolSearchV2.propTypes = {
  id: string.isRequired,
  onSelect: func.isRequired,
  hasError: bool.isRequired,
  searchUrl: string.isRequired,
  iamApiUrl: string.isRequired,
  brand: shape({
    short: string.isRequired,
    full: string.isRequired,
  }).isRequired,
  onBlur: func,
  onChange: func,
  disabled: bool,
  value: string,
  width: string,
  maxLength: number,
};

SchoolSearchV2.defaultProps = {
  value: '',
  width: '420px',
  onChange: () => { },
  onBlur: () => { },
  disabled: false,
  maxLength: 150,
};
